/**
 * The Technology ROI Calculator in Turkish, written 2026-08-30 on Kagan's
 * request — the tool's own words, not the page frame around it, which is in
 * `content/pages/tr/tools-roi-calculator.ts`.
 *
 * A deep partial of `roiCalculator`, the same way `diagnostic.tr.ts` is of the
 * Diagnostic. Only strings: the model, the industry percentages and the
 * objective modifiers are keyed by English and untouched by anything here.
 *
 * Written rather than translated, per the brief: business words a Turkish
 * executive already uses in English stay in English — ROI, EBITDA, OEE, NRR,
 * ARPU, KPI, Run / Grow / Transform, Forward Deployed Engineer — while the
 * sentences around them are Turkish. "Tangible / intangible" become "somut /
 * soyut", which is what a CFO says.
 */
export const roiCalculatorTr = {
  form: {
    revenue: 'Yıllık ciro (USD)',
    revenuePlaceholder: 'örn. 250.000.000',
    country: 'Ülke',
    industry: 'Sektör',
    objective: 'İş hedefi',
    objectiveHint: 'birden fazla seçebilirsiniz',
    submit: 'ROI’yi hesapla',
    privacy: 'Girdileriniz, raporunuzu almayı seçmediğiniz sürece kaydedilmez.',
    needRevenue: 'Lütfen yıllık cironuzu USD olarak girin (en az 1 milyon $).',
    needObjective: 'Lütfen en az bir iş hedefi seçin.',
  },

  industries: {
    banking: {
      name: 'Bankacılık & Finansal Hizmetler',
      kpi: 'Gider / Gelir (C/I) oranı',
      kdesc: 'Bankacılıkta dijital liderler, C/I oranını rakiplerinin 5–10 puan altında çalıştırıyor. Maliyet tabanınızda tek puanlık bir iyileşme bile çoğu zaman tüm dönüşüm bütçesini gölgede bırakır.',
    },
    software: {
      name: 'Yazılım & İnternet Hizmetleri',
      kpi: 'Net Revenue Retention (NRR)',
      kdesc: 'NRR’deki her puan yıllık olarak birikerek büyür. Sistematik dönüşüm; ürün, veri ve GTM yatırımlarını tam da retention’ın kazanıldığı yere odaklar.',
    },
    education: {
      name: 'Eğitim',
      kpi: 'Kayıt sayısı & öğrenci tutundurma',
      kdesc: 'Dijital olgunluğu yüksek kurumlar, daha düşük kazanım maliyetiyle daha çok öğrenciyi kazanıyor ve elinde tutuyor.',
    },
    media: {
      name: 'Medya & Eğlence',
      kpi: 'ARPU & izleyici tutundurma',
      kdesc: 'Veriye dayalı içerik ve kişiselleştirme, kullanıcı başına geliri yükseltir ve churn’ü düşürür.',
    },
    healthcare: {
      name: 'Sağlık Hizmeti Sağlayıcıları',
      kpi: 'Hasta epizodu başına maliyet',
      kdesc: 'Dijital olgunluğu yüksek sağlık kuruluşları aynı kapasiteyle daha fazla hastaya bakıyor; epizot başına maliyeti düşürürken klinik sonuçları iyileştiriyor.',
    },
    telecom: {
      name: 'Telekomünikasyon',
      kpi: 'ARPU & churn oranı',
      kdesc: 'Dönüşümde önde olanlar şebekeyi ve veriyi daha hızlı paraya çeviriyor, daha az müşteri kaybediyor.',
    },
    profserv: {
      name: 'Profesyonel Hizmetler',
      kpi: 'Utilization & profesyonel başına gelir',
      kdesc: 'Yapay zekâ destekli teslimat, faturalanabilir utilization’ı ve profesyonel başına geliri yükseltir.',
    },
    utilities: {
      name: 'Utilities (Elektrik, Su, Doğalgaz)',
      kpi: 'Hizmet maliyeti & şebeke güvenilirliği (SAIDI)',
      kdesc: 'Akıllı operasyon, hizmet maliyetini ve kesinti dakikalarını aynı anda düşürür.',
    },
    pharma: {
      name: 'İlaç & Yaşam Bilimleri',
      kpi: 'Pazara çıkış süresi / Ar-Ge döngüsü',
      kdesc: 'Geliştirme takviminden kazanılan her ay, erken gelen gelir olarak dönüşüm bütçesinin katlarına denk gelir.',
    },
    insurance: {
      name: 'Sigorta',
      kpi: 'Combined ratio',
      kdesc: 'Veri ve yapay zekâ olgunluğu fiyatlamayı, hasar süreçlerini ve sahtecilik tespitini iyileştirir; combined ratio’daki her puan doğrudan kâra yazılır.',
    },
    transport: {
      name: 'Taşımacılık & Lojistik',
      kpi: 'Zamanında teslim & sevkiyat başına maliyet',
      kdesc: 'Uçtan uca görünürlük ve optimizasyon, varlık kullanımını ve teslimat güvenilirliğini yükseltir.',
    },
    consumer: {
      name: 'Tüketim Ürünleri',
      kpi: 'Tahmin doğruluğu & OTIF',
      kdesc: 'Daha iyi talep tahmini, rafta bulunurluğu korurken stoksuz kalmayı ve fireyi azaltır.',
    },
    indelec: {
      name: 'Endüstriyel Elektronik & Elektrikli Ekipman',
      kpi: 'OEE (Overall Equipment Effectiveness)',
      kdesc: 'Geri kazanılan her OEE puanı, bedelini zaten ödediğiniz kapasitedir. Dijital liderler, geride kalanların 10+ puan üzerinde çalışıyor.',
    },
    manufacturing: {
      name: 'Endüstriyel Üretim',
      kpi: 'OEE (Overall Equipment Effectiveness)',
      kdesc: 'Geri kazanılan her OEE puanı, bedelini zaten ödediğiniz kapasitedir. Dijital liderler, geride kalanların 10+ puan üzerinde çalışıyor.',
    },
    chemicals: {
      name: 'Kimya',
      kpi: 'Varlık kullanımı & verim',
      kdesc: 'Proses optimizasyonu ve öngörücü kalite, doğrudan ton başına marja dönüşür.',
    },
    food: {
      name: 'Gıda & İçecek Üretimi',
      kpi: 'OEE & fire oranı',
      kdesc: 'Hat verimliliği ve fire azaltımı, gıda üretiminde en hızlı somut kazançlardır.',
    },
    retail: {
      name: 'Perakende & Toptan',
      kpi: 'Metrekare başına satış',
      kdesc: 'Omnichannel olgunluğu, perakende kârlılığını belirleyen iki kaldıracı birlikte yükseltir: satış yoğunluğu ve stok devir hızı.',
    },
    construction: {
      name: 'İnşaat & Yapı Malzemeleri',
      kpi: 'Proje marjı & yeniden iş oranı',
      kdesc: 'Dijital proje kontrolü, proje marjının sessiz katilleri olan yeniden işi ve hak taleplerini azaltır.',
    },
    energy: {
      name: 'Enerji',
      kpi: 'Üretim sürekliliği & birim maliyet',
      kdesc: 'Öngörücü operasyon, enerjideki en büyük değer kaldıracı olan çalışma süresini korur.',
    },
    other: {
      name: 'Diğer / Sektörler Arası',
      kpi: 'EBITDA marjı',
      kdesc: 'Tüm sektörlerde dijital olgunluğu yüksek şirketler rakiplerinden daha hızlı büyüyor ve gelirin daha büyük kısmını kâra çeviriyor.',
    },
  },

  countries: {
    'United States': 'Amerika Birleşik Devletleri',
    'United Kingdom': 'Birleşik Krallık',
    Germany: 'Almanya',
    France: 'Fransa',
    Netherlands: 'Hollanda',
    Belgium: 'Belçika',
    Switzerland: 'İsviçre',
    Austria: 'Avusturya',
    Italy: 'İtalya',
    Spain: 'İspanya',
    Portugal: 'Portekiz',
    Ireland: 'İrlanda',
    'Nordics (SE/NO/DK/FI)': 'İskandinavya (İsveç/Norveç/Danimarka/Finlandiya)',
    Poland: 'Polonya',
    Czechia: 'Çekya',
    Romania: 'Romanya',
    Greece: 'Yunanistan',
    'United Arab Emirates': 'Birleşik Arap Emirlikleri',
    'Saudi Arabia': 'Suudi Arabistan',
    Qatar: 'Katar',
    Kuwait: 'Kuveyt',
    Egypt: 'Mısır',
    'South Africa': 'Güney Afrika',
    Nigeria: 'Nijerya',
    Kenya: 'Kenya',
    Israel: 'İsrail',
    Canada: 'Kanada',
    Mexico: 'Meksika',
    Brazil: 'Brezilya',
    Argentina: 'Arjantin',
    Chile: 'Şili',
    Colombia: 'Kolombiya',
    India: 'Hindistan',
    China: 'Çin',
    Japan: 'Japonya',
    'South Korea': 'Güney Kore',
    Singapore: 'Singapur',
    'Hong Kong': 'Hong Kong',
    Australia: 'Avustralya',
    'New Zealand': 'Yeni Zelanda',
    Indonesia: 'Endonezya',
    Malaysia: 'Malezya',
    Thailand: 'Tayland',
    Vietnam: 'Vietnam',
    Philippines: 'Filipinler',
    Pakistan: 'Pakistan',
    Kazakhstan: 'Kazakistan',
    Azerbaijan: 'Azerbaycan',
    'Other (Developed Economy)': 'Diğer (gelişmiş ekonomi)',
    'Other (Emerging Economy)': 'Diğer (gelişmekte olan ekonomi)',
  },

  objectives: {
    Growth: { label: 'Büyüme', note: 'dijital kanallar, ürünler ve pazarlarla gelir büyümesi' },
    'Cost Saving': { label: 'Maliyet Tasarrufu', note: 'operasyon genelinde verimlilik ve otomasyon' },
    'Operational Excellence': {
      label: 'Operasyonel Mükemmellik',
      note: 'süreç performansı, kalite ve güvenilirlik',
    },
    'Customer Centricity': {
      label: 'Müşteri Odaklılık',
      note: 'deneyim, müşteri tutundurma ve yaşam boyu değer',
    },
    'Technology Transformation': {
      label: 'Teknoloji Dönüşümü',
      note: 'modern platformlar, veri ve yapay zekâ temelleri',
    },
    'Cultural Transformation': {
      label: 'Kültürel Dönüşüm',
      note: 'yetkinlikler, çalışma biçimleri ve benimseme',
    },
    Other: { label: 'Diğer', note: 'kendi stratejik gündeminiz' },
  },

  teaser: {
    eyebrow: 'DEĞER POTANSİYELİNİZ',
    heading: 'Teknoloji yatırımınız katlarıyla geri dönebilir: işte somut kısmı',
    basis:
      '{industry} şirketleri cirolarının yaklaşık %{pct}’ini teknolojiye ayırıyor '
      + '({country} için düzeltilmiş), sizde bu yılda tahminen {budget} ediyor. '
      + 'Bütçe girdidir; asıl mesele geri döndürdüğü değerdir.',
    gtLabel: 'Değer üreten teknoloji bütçesi',
    gtNote:
      'Teknoloji bütçenizin Grow & Transform kısmı. Kalan %{run} işi ayakta tutuyor: '
      + 'gerekli, ama yeni bir değer üretmiyor.',
    tangibleLabel: 'Yıllık somut değer potansiyeli',
    tangibleNote:
      'Ölçülebilen ve doğrudan bağlanabilen getiriler: operasyonel verimlilik, marj artışı, yeni dijital gelir.',
    ratioLabel: 'Toplam değer oranı',
    ratioNote:
      'Teknolojiye harcadığınız her 1 $, somut değer, soyut değer ve önlenen riskle birlikte bu kadar geri dönebilir. Dökümün tamamını aşağıdan açın.',
    run: 'Run',
    grow: 'Grow',
    transform: 'Transform',
    unlock: 'Sonuçlarımın tamamını gör',
  },

  report: {
    eyebrow: 'SONUÇLARINIZIN TAMAMI',
    heading: 'Yıllık teknoloji değer potansiyeliniz',
    assumptions:
      'Varsayımlar: {budget} tutarındaki değer üreten teknoloji bütçenizde {multiple}x somut '
      + 'getiri, hedeflerinize göre düzeltilmiş ({objectives}). Sektör benchmark’ları ve '
      + 'gerçek değer vakalarıyla kalibre edilmiştir.',
    tangible: 'Somut faydalar',
    tangibleBody:
      'Grow & Transform bütçenizi {objectives} üzerine odaklamanızın yılda {tangible} '
      + 'getireceğini tahmin ediyoruz, ölçüsü {kpi}.',
    and: 've',
    intangible: 'Soyut faydalar',
    opportunity: 'Önlenen fırsat maliyeti',
    total: 'Toplam yıllık fayda',
    digiRoiLabel: 'Digitopia ROI’niz',
    digiRoiNote:
      'Digitopia’ya yaptığınız yatırıma karşılık toplam yıllık değer, teknoloji '
      + 'bütçenizin %{share}’inden azı.',
    ratioLabel: 'Teknoloji yatırımınızın toplam değer oranı',
    ratioNote:
      'Dönüşüm ad hoc değil sistematik yönetildiğinde, teknoloji bütçenizin her doları için elde edilen toplam yıllık değer: somut, soyut ve önlenen risk.',
    intangibleBody: [
      '**Doğru kararlar.** Yanlış ya da eksik bilgiyle alınan tek bir stratejik karar size neye mal olur? Daha iyi veri kalitesi ve karar zekâsı, milyonlarca dolarlık yanlış adımları baştan engeller.',
      '**Yönetim ekibinde hizalanma.** Hizalanmamış liderlik ekipleri bütçeyi birbiriyle yarışan önceliklere harcar. Hizalanma, aynı harcamayı birikerek büyüyen sonuçlara çevirir.',
      '**Hızlanan icra.** Büyük bir yatırım her çeyrek geciktiğinde getirisi de onunla birlikte gecikir. Kurumsal çeviklik değeri öne çeker.',
    ],
    intangibleAlso:
      'Hesaba katılan diğerleri: çalışan deneyimi, bilgi sermayesi, marka ve güven, ekosistem etkileri.',
    opportunityBody:
      'Beklemek de bir karardır ve beklenen bir bedeli vardır: veri borcu, yetenek kaybı, rekabette geri düşme, regülasyon riski, kırılgan tedarik zinciri ve inovasyonun durması.',
    opportunityEstimate:
      'Kurumunuz için dönüşmemenin beklenen yıllık maliyetini {amount} olarak tahmin '
      + 'ediyoruz, çoğu zaman teknoloji bütçesinin kendisinden büyük. Yatırım yapmamak '
      + 'en pahalı seçenektir.',
    restart: 'Baştan başla',
    ctaHeading: 'Sayılar başlangıç noktasıdır. Asıl varılacak yer yol haritanızdır.',
    ctaBody:
      '30 dakikalık bir görüşmede sonuçlarınızı birlikte gözden geçirir, sizi küresel veri setimizdeki 1.575 şirketle kıyaslar ve bir DAIMI olgunluk ölçümünün kurumunuz hakkında ne göstereceğini paylaşırız.',
    ctaLabel: 'Bize Ulaşın',
    disclaimer:
      'Tahminler sektör benchmark’ları ve gerçek değer vakalarıyla kalibre edilmiştir. Resmî bir iş vakası değil, yön gösteren bir hesaptır.',
  },
}
